import { zodResolver } from "@hookform/resolvers/zod";
import { notifications } from "@marketplace/ui/notifications";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { useAtom } from "jotai";
import { useEffect, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { AUTH_SESSION_KEYS } from "@/constants/auth-session-keys";
import {
  loginOtpEmailAtom,
  loginOtpExpirationTimeAtom
} from "../../atoms/login-atom";
import type { LoginEmailResponseDto } from "../../dtos/login-email-response-dto";
import { OtpService } from "../../services/otp-service";
import { usePostVerifyOtpMutation } from "../mutations/use-post-verify-otp-mutation";
import {
  useVerifyOtpSchema,
  type VerifyOtpSchema
} from "../schemas/use-verify-otp-schema";
import { useLogin } from "../use-login";

const routeApi = getRouteApi("/auth/verify");

export const useVerifyOtpForm = () => {
  const navigate = useNavigate();
  const searchParams = routeApi.useSearch();
  const page = searchParams.page;

  const [encryptionLoading, setEncryptionLoading] = useState(true);
  const [email, setEmail] = useAtom(loginOtpEmailAtom);
  const [expirationTime, setExpirationTime] = useAtom(
    loginOtpExpirationTimeAtom
  );

  useEffect(() => {
    (async () => {
      try {
        setEncryptionLoading(true);
        const token = sessionStorage.getItem(AUTH_SESSION_KEYS.OTP_TOKEN);
        if (!token) {
          throw new Error("No token found");
        }
        const { email, expirationTime } =
          await OtpService.getInstance().decrypt(token);

        setEmail(email);
        setExpirationTime(expirationTime);
      } catch {
        notifications.show({
          message: "Invalid token. Please try logging in again.",
          variant: "error"
        });
        navigate({ to: "/auth/login" });
      } finally {
        setEncryptionLoading(false);
      }
    })();
  }, [navigate, setEmail, setExpirationTime]);

  const { t: tAuth } = useTranslation("auth");
  const verifyOtpSchema = useVerifyOtpSchema();
  const { mutateAsync, isPending } = usePostVerifyOtpMutation();
  const login = useLogin();

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors }
  } = useForm<VerifyOtpSchema>({
    resolver: zodResolver(verifyOtpSchema)
  });

  const handleSignIn = async (data: LoginEmailResponseDto["data"]) => {
    try {
      await login({
        accessToken: data.access_token,
        userData: data.user
      });

      sessionStorage.removeItem(AUTH_SESSION_KEYS.OTP_TOKEN);

      notifications.show({
        message: tAuth("notification.login.loginSuccess")
      });

      window.location.href = "/";
    } catch {
      setError("otp", {
        message: "OTP verification failed. Please try again.",
        type: "server"
      });
    }
  };

  const onSubmit: SubmitHandler<VerifyOtpSchema> = async (values) => {
    try {
      if (!email) return;

      const res = await mutateAsync({
        email,
        otp_code: values.otp,
        page
      });

      if (res && !res.errors) {
        if (page === "register") {
          sessionStorage.removeItem(AUTH_SESSION_KEYS.OTP_TOKEN);
          notifications.show({
            message: tAuth("notification.register.registerSuccess")
          });
          navigate({ to: "/auth/login" });
        } else if (page === "login") {
          handleSignIn(res.data);
        }
      }
    } catch {
      setError("otp", {
        message: "OTP verification failed. Please try again.",
        type: "server"
      });
    }
  };

  return {
    control,
    handleSubmit: handleSubmit(onSubmit),
    errors,
    isPending,
    encryptionLoading,
    email,
    expiredAt: expirationTime
  };
};
